import { useCallback, useContext } from 'react';
import type { CompleteMultipartUploadOutput } from '@aws-sdk/client-s3';
import { S3 } from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import type { UploadCredential } from '@trinity/chat';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useTelemetryLogger } from '@trinity/observability-web/react';

// Returns a stable callback that uploads a single file directly to S3 using short-lived STS credentials
// issued by the upload-credentials endpoint.
export function useUploadInputAttachment(): (file: File, credential: UploadCredential) => Promise<void> {
  const dispatch = useContext(ChatStateDispatchContext);
  const loggerCtx = useTelemetryLogger();

  return useCallback(
    async (file: File, credential: UploadCredential): Promise<void> => {
      try {
        // STS AssumeRole scoped to s3:PutObject on the chat-input-attachments/ prefix.
        const client = new S3({
          credentials: {
            ...credential.credentials,
            expiration: new Date(credential.credentials.expiration),
          },
          region: credential.region,
          useAccelerateEndpoint: true,
          // Only compute/validate checksums when S3 requires them.
          requestChecksumCalculation: 'WHEN_REQUIRED',
          responseChecksumValidation: 'WHEN_REQUIRED',
        });

        const upload = new Upload({
          client,
          params: {
            Body: file,
            Bucket: credential.bucket,
            Key: credential.s3Key,
            ContentType: file.type,
          },
        });

        // Report per-chunk progress so the UI chip can show a progress bar.
        upload.on('httpUploadProgress', (event) => {
          if (!event.total || event.loaded === undefined) return;
          dispatch({
            type: 'pending-attachment/upload-progress',
            payload: {
              identifier: credential.identifier,
              progress: Math.ceil((event.loaded * 100) / event.total),
            },
          });
        });

        const result = await upload.done();
        const checksum = (result as CompleteMultipartUploadOutput).ChecksumSHA256 ?? undefined;
        const fileUri = `s3://${credential.bucket}/${credential.s3Key}`;

        dispatch({
          type: 'pending-attachment/upload-succeeded',
          payload: { identifier: credential.identifier, fileUri, checksum },
        });
      } catch (e) {
        // Capture to Sentry before dispatching so the error is always recorded.
        loggerCtx.captureException(e);
        dispatch({
          type: 'pending-attachment/upload-failed',
          payload: {
            identifier: credential.identifier,
            errorMessage: e instanceof Error ? e.message : undefined,
          },
        });
        throw e;
      }
    },
    [dispatch, loggerCtx]
  );
}
